import { useState, useRef, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { ChevronLeft, User, Mail, Phone, Edit, Save, X, Camera, Shield, Calendar, Lock, Smartphone, CheckCircle, ExternalLink } from "lucide-react"
import { Button, Card, CardBody, CardHeader, Input, Avatar, Divider, Chip } from "@heroui/react"
import { useAuth } from "@src/presentation/contexts/AuthContext"
import { useUpdateProfile, useUploadProfilePicture } from "@src/presentation/hooks/users"
import { ToastService } from "@src/shared/services/toast.service"
import { CreditDetailsCard } from "@src/presentation/components/CreditDetailsCard"
import { useQueryClient } from "@tanstack/react-query"
import { WhatsAppLinkBanner } from "@src/presentation/components/WhatsAppLinkBanner"

export default function ProfilePage() {
    const navigate = useNavigate()
    const { user } = useAuth()
    const queryClient = useQueryClient()
    const [isEditing, setIsEditing] = useState(false)
    const fileInputRef = useRef<HTMLInputElement>(null)

    const [editedData, setEditedData] = useState({
        first_name: user?.firstName || "",
        middle_name: user?.middleName || "",
        last_name: user?.lastName || "",
        phone_number: user?.phone || "",
    })

    // Sync editedData with user data when it loads or changes
    useEffect(() => {
        if (user) {
            setEditedData({
                first_name: user.firstName || "",
                middle_name: user.middleName || "",
                last_name: user.lastName || "",
                phone_number: user.phone || "",
            })
        }
    }, [user])

    // Update profile mutation
    const { mutate: updateProfile, isPending: isUpdating } = useUpdateProfile({
        onSuccess: async () => {
            ToastService.success("Profile updated successfully")
            setIsEditing(false)
            // Refetch current user data to ensure UI is in sync with server
            await queryClient.invalidateQueries({ queryKey: ['auth', 'currentUser'] })
        },
        onError: (error) => {
            ToastService.error(error.message || "Failed to update profile")
        }
    })

    // Upload profile picture mutation
    const { mutate: uploadPicture, isPending: isUploading } = useUploadProfilePicture({
        onSuccess: async () => {
            ToastService.success("Profile picture updated successfully")
            // Refetch current user data to get the new profile picture URL
            await queryClient.invalidateQueries({ queryKey: ['auth', 'currentUser'] })
        },
        onError: (error) => {
            ToastService.error(error.message || "Failed to upload profile picture")
        }
    })

    const handleSave = () => {
        updateProfile(editedData)
    }

    const handleCancel = () => {
        setEditedData({
            first_name: user?.firstName || "",
            middle_name: user?.middleName || "",
            last_name: user?.lastName || "",
            phone_number: user?.phone || "",
        })
        setIsEditing(false)
    }

    const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0]
        if (!file) return

        // Validate file type
        const validTypes = ["image/jpeg", "image/png", "image/gif", "image/webp"]
        if (!validTypes.includes(file.type)) {
            ToastService.error("Please select a valid image file (JPEG, PNG, GIF, or WebP)")
            return
        }

        // Validate file size (5MB)
        if (file.size > 5 * 1024 * 1024) {
            ToastService.error("Image size must be less than 5MB")
            return
        }

        // Upload the file
        uploadPicture({ file })
    }

    const triggerFileInput = () => {
        fileInputRef.current?.click()
    }

    return (
        <div className="bg-background overflow-auto h-full pb-8">
            {/* Header */}
            <header className="border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 sticky top-0 z-20">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-col sm:flex-row min-h-[56px] sm:h-16 py-2 sm:py-0 items-start sm:items-center justify-between gap-2 sm:gap-0">
                        <div className="flex items-center gap-3">
                            <Button
                                isIconOnly
                                variant="light"
                                size="sm"
                                onPress={() => navigate(-1)}
                                className="flex-shrink-0"
                            >
                                <ChevronLeft className="h-5 w-5" />
                            </Button>
                            <h1 className="text-xl font-semibold text-foreground">My Profile</h1>
                        </div>
                        <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
                            {isEditing ? (
                                <>
                                    <Button
                                        color="default"
                                        variant="flat"
                                        onPress={handleCancel}
                                        startContent={<X className="h-4 w-4" />}
                                        isDisabled={isUpdating}
                                        className="rounded flex-1 sm:flex-none"
                                        size="sm"
                                    >
                                        <span className="hidden sm:inline">Cancel</span>
                                        <span className="sm:hidden">Cancel</span>
                                    </Button>
                                    <Button
                                        color="primary"
                                        onPress={handleSave}
                                        startContent={<Save className="h-4 w-4" />}
                                        isLoading={isUpdating}
                                        className="rounded flex-1 sm:flex-none"
                                        size="sm"
                                    >
                                        <span className="hidden sm:inline">Save Changes</span>
                                        <span className="sm:hidden">Save</span>
                                    </Button>
                                </>
                            ) : (
                                <Button
                                    color="primary"
                                    onPress={() => setIsEditing(true)}
                                    startContent={<Edit className="h-4 w-4" />}
                                    className="rounded flex-1 sm:flex-none"
                                    size="sm"
                                >
                                    <span className="hidden sm:inline">Edit Profile</span>
                                    <span className="sm:hidden">Edit</span>
                                </Button>
                            )}
                        </div>
                    </div>
                </div>
            </header>

            {/* Main Content */}
            <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-8 max-w-5xl">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Left Column - Profile Card */}
                    <div className="lg:col-span-1">
                        <Card className="border border-border rounded shadow-none">
                            <CardBody className="p-6">
                                <div className="flex flex-col items-center">
                                    <div className="relative mb-4">
                                        <Avatar
                                            src={user?.profilePictureUrl || undefined}
                                            name={user?.displayName}
                                            className="w-32 h-32 text-2xl"
                                            showFallback
                                        />
                                        <input
                                            ref={fileInputRef}
                                            type="file"
                                            accept="image/jpeg,image/png,image/gif,image/webp"
                                            onChange={handleFileSelect}
                                            className="hidden"
                                        />
                                        <Button
                                            isIconOnly
                                            size="sm"
                                            color="primary"
                                            variant="solid"
                                            className="absolute bottom-0 right-0"
                                            onPress={triggerFileInput}
                                            isLoading={isUploading}
                                        >
                                            <Camera className="h-4 w-4" />
                                        </Button>
                                    </div>

                                    <h2 className="text-xl font-bold text-foreground text-center">
                                        {user?.displayName}
                                    </h2>
                                    <p className="text-sm text-default-500 mt-1">{user?.email}</p>

                                    <Divider className="my-4" />

                                    {/* Roles and Status */}
                                    <div className="w-full space-y-3">
                                        <div>
                                            <p className="text-xs text-default-500 font-medium mb-2">Roles</p>
                                            <div className="flex flex-wrap gap-1.5 justify-center">
                                                {user?.roles.map((role, index) => (
                                                    <Chip key={index} color="primary" variant="flat" size="sm" startContent={<Shield className="h-3 w-3" />}>
                                                        {role.displayName}
                                                    </Chip>
                                                ))}
                                            </div>
                                        </div>

                                        <div>
                                            <p className="text-xs text-default-500 font-medium mb-2">Account Status</p>
                                            <div className="flex flex-wrap gap-1.5 justify-center">
                                                <Chip
                                                    size="sm"
                                                    variant="flat"
                                                    color={user?.isActive ? "success" : "danger"}
                                                >
                                                    {user?.isActive ? "Active" : "Inactive"}
                                                </Chip>
                                                {user?.isVerified && (
                                                    <Chip color="success" variant="flat" size="sm">
                                                        Verified
                                                    </Chip>
                                                )}
                                                {user?.isBanned && (
                                                    <Chip color="danger" variant="flat" size="sm">
                                                        Banned
                                                    </Chip>
                                                )}
                                            </div>
                                        </div>

                                        <Divider />

                                        <div className="flex items-center gap-2 text-xs text-default-500">
                                            <Calendar className="h-3 w-3" />
                                            <span>Joined {new Date(user?.createdAt || Date.now()).toLocaleDateString("en-US", { month: "short", year: "numeric" })}</span>
                                        </div>
                                    </div>
                                </div>
                            </CardBody>
                        </Card>
                    </div>

                    {/* Right Column - Edit Form */}
                    <div className="lg:col-span-2 space-y-6">
                        {/* Personal Information Card */}
                        <Card className="border border-border rounded shadow-none">
                            <CardHeader className="border-b border-divider pb-4">
                                <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                                    <User className="h-4 w-4" />
                                    Personal Information
                                </h3>
                            </CardHeader>
                            <CardBody className="p-6">
                                <div className="grid gap-4">
                                    {isEditing ? (
                                        <>
                                            <Input
                                                label="First Name"
                                                value={editedData.first_name}
                                                onChange={(e) => setEditedData({ ...editedData, first_name: e.target.value })}
                                                startContent={<User className="h-4 w-4 text-default-400" />}
                                                isRequired
                                                classNames={{
                                                    inputWrapper: 'rounded border border-border'
                                                }}
                                            />
                                            <Input
                                                label="Middle Name (Optional)"
                                                value={editedData.middle_name}
                                                onChange={(e) => setEditedData({ ...editedData, middle_name: e.target.value })}
                                                startContent={<User className="h-4 w-4 text-default-400" />}
                                                classNames={{
                                                    inputWrapper: 'rounded border border-border'
                                                }}
                                            />
                                            <Input
                                                label="Last Name"
                                                value={editedData.last_name}
                                                onChange={(e) => setEditedData({ ...editedData, last_name: e.target.value })}
                                                startContent={<User className="h-4 w-4 text-default-400" />}
                                                isRequired
                                                classNames={{
                                                    inputWrapper: 'rounded border border-border'
                                                }}
                                            />
                                            <Input
                                                label="Phone Number"
                                                value={editedData.phone_number}
                                                onChange={(e) => setEditedData({ ...editedData, phone_number: e.target.value })}
                                                startContent={<Phone className="h-4 w-4 text-default-400" />}
                                                type="tel"
                                                placeholder="+233XXXXXXXXX"
                                                classNames={{
                                                    inputWrapper: 'rounded border border-border'
                                                }}
                                            />
                                        </>
                                    ) : (
                                        <>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                <div className="flex items-start gap-3 p-3 rounded border border-border bg-default-50/50">
                                                    <User className="h-4 w-4 text-default-500 mt-0.5" />
                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-xs text-default-500 font-medium">First Name</p>
                                                        <p className="text-sm font-medium text-foreground truncate">{user?.firstName}</p>
                                                    </div>
                                                </div>

                                                {user?.middleName && (
                                                    <div className="flex items-start gap-3 p-3 rounded border border-border bg-default-50/50">
                                                        <User className="h-4 w-4 text-default-500 mt-0.5" />
                                                        <div className="flex-1 min-w-0">
                                                            <p className="text-xs text-default-500 font-medium">Middle Name</p>
                                                            <p className="text-sm font-medium text-foreground truncate">{user.middleName}</p>
                                                        </div>
                                                    </div>
                                                )}

                                                <div className="flex items-start gap-3 p-3 rounded border border-border bg-default-50/50">
                                                    <User className="h-4 w-4 text-default-500 mt-0.5" />
                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-xs text-default-500 font-medium">Last Name</p>
                                                        <p className="text-sm font-medium text-foreground truncate">{user?.lastName}</p>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="flex items-start gap-3 p-3 rounded border border-border bg-default-50/50">
                                                <Mail className="h-4 w-4 text-default-500 mt-0.5" />
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-xs text-default-500 font-medium">Email Address</p>
                                                    <p className="text-sm font-medium text-foreground break-all">{user?.email}</p>
                                                </div>
                                            </div>

                                            {user?.phone && (
                                                <div className="flex items-start gap-3 p-3 rounded border border-border bg-default-50/50">
                                                    <Phone className="h-4 w-4 text-default-500 mt-0.5" />
                                                    <div className="flex-1">
                                                        <p className="text-xs text-default-500 font-medium">Phone Number</p>
                                                        <p className="text-sm font-medium text-foreground">{user.phone}</p>
                                                    </div>
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            </CardBody>
                        </Card>

                        {/* Credit Balance Card */}
                        <CreditDetailsCard />

                        {/* WhatsApp Connection Card */}
                        <Card className="border border-border rounded shadow-none">
                            <CardHeader className="border-b border-divider pb-4">
                                <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                                    <Smartphone className="h-4 w-4" />
                                    WhatsApp Connection
                                </h3>
                            </CardHeader>
                            <CardBody className="p-4">
                                {user?.whatsappLinked ? (
                                    <div className="flex flex-col gap-3">
                                        <div className="flex items-center gap-3 p-3 rounded-lg bg-[#25D366]/5 border border-[#25D366]/20">
                                            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-[#25D366]/10">
                                                <CheckCircle className="h-5 w-5 text-[#25D366]" />
                                            </div>
                                            <div className="flex-1">
                                                <p className="text-sm font-medium text-foreground">WhatsApp Linked</p>
                                                <p className="text-xs text-default-500">You can access PASCOs directly from WhatsApp</p>
                                            </div>
                                        </div>
                                        <Button
                                            variant="flat"
                                            className="w-full rounded-lg bg-[#25D366]/10 text-[#25D366] hover:bg-[#25D366]/20"
                                            startContent={<ExternalLink className="h-4 w-4" />}
                                            onPress={() => window.open('https://wa.me/233542480050', '_blank', 'noopener,noreferrer')}
                                        >
                                            Open WhatsApp Chat
                                        </Button>
                                    </div>
                                ) : (
                                    <WhatsAppLinkBanner variant="prominent" />
                                )}
                            </CardBody>
                        </Card>

                        {/* Quick Actions */}
                        <Card className="border border-border rounded shadow-none">
                            <CardHeader className="border-b border-divider pb-4">
                                <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                                    <Lock className="h-4 w-4" />
                                    Quick Actions
                                </h3>
                            </CardHeader>
                            <CardBody className="p-4">
                                <div className="grid grid-cols-1 gap-2">
                                    <Button
                                        fullWidth
                                        variant="flat"
                                        className="justify-start rounded"
                                        startContent={<Shield className="h-4 w-4" />}
                                        onPress={() => navigate('/account/security')}
                                    >
                                        Security Settings
                                    </Button>
                                </div>
                            </CardBody>
                        </Card>
                    </div>
                </div>
            </main>
        </div>
    )
}
